import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { ReactComponent as StopIcon } from "../assets/icon_stop.svg";
import { ReactComponent as PlayIcon } from "../assets/icon_play.svg";
import { formatDuration } from "../utils/formatDuration";
import { getDeviceId } from "../utils/deviceId";
import { isAiInvoiceEnabled } from "../utils/billingRateSetting";

// The tracking tray, above the tab selector. Mirrors Android's TrackedTaskBar:
// up to 5 rows of currently-tracked AND recently-tracked tasks (cross-project),
// active first. Each row shows a meta line — "<project> • <subtitle> <time>" —
// over the task title, where subtitle is "New Record" (running), "Total Spent"
// (stopped, time = sum of records), or "Tracking on another device" (foreign).
// Running rows have a stop button + live timer; stopped rows have a play
// (resume) button and a ✕ to dismiss.
const TrackingTray = () => {
  const {
    trackedTasks,
    startCollectingTrackedTasks,
    requestStopTracking,
    dismissTrackedTask,
    setTaskTrackingState,
    setTaskAiTrackingState,
    getTaskTotalSpent,
    getActionLabel,
    projects,
  } = useContext(FirebaseContext);
  const navigate = useNavigate();

  const [now, setNow] = useState(Date.now());
  const [totals, setTotals] = useState({}); // taskId -> total spent ms
  const [actionLabels, setActionLabels] = useState({}); // taskId -> action label

  // The action currently being tracked for a row (human timer takes priority),
  // or null when the timer is task-scoped.
  const trackedActionIdOf = (t) =>
    t.taskBeingTracked
      ? t.trackedActionId ?? null
      : t.aiBeingTracked
      ? t.aiTrackedActionId ?? null
      : null;

  useEffect(() => {
    startCollectingTrackedTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActive = (t) => t.taskBeingTracked || t.aiBeingTracked;
  const anyActive = (trackedTasks || []).some(isActive);
  useEffect(() => {
    if (!anyActive) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [anyActive]);

  // Fetch total-spent for the stopped rows (the "Total Spent" label).
  useEffect(() => {
    (trackedTasks || [])
      .filter((t) => !isActive(t))
      .forEach(async (t) => {
        const ms = await getTaskTotalSpent(t.id);
        setTotals((prev) =>
          prev[t.id] === ms ? prev : { ...prev, [t.id]: ms }
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedTasks]);

  // Resolve the tracked action's label per row (for the "<task> · <action>"
  // title). Cleared to "" when the row isn't tracking a specific action.
  useEffect(() => {
    (trackedTasks || []).forEach(async (t) => {
      const actionId = trackedActionIdOf(t);
      const label = actionId == null ? "" : await getActionLabel(actionId);
      setActionLabels((prev) =>
        prev[t.id] === label ? prev : { ...prev, [t.id]: label }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedTasks]);

  if (!trackedTasks || trackedTasks.length === 0) return null;

  const deviceId = getDeviceId();
  const aiVisible = isAiInvoiceEnabled();
  const projectName = (projectId) =>
    projects.find((p) => Number(p.id) === Number(projectId))?.projectName || "";

  return (
    <div style={styles.container}>
      {trackedTasks.map((task) => {
        const humanActive = !!task.taskBeingTracked;
        const aiActive = !!task.aiBeingTracked;
        const active = humanActive || aiActive;
        const showAi = aiVisible && task.aiTrackingEnabled !== false;
        const isForeign =
          (humanActive &&
            task.trackingDeviceId &&
            task.trackingDeviceId !== deviceId) ||
          (aiActive &&
            task.aiTrackingDeviceId &&
            task.aiTrackingDeviceId !== deviceId);
        // Live elapsed for whichever tracker is running (human takes priority
        // for the single time label); total spent when idle.
        const elapsedFrom = humanActive
          ? task.trackStartedTimestamp
          : task.aiTrackStartedTimestamp;
        const timeLabel = active
          ? formatDuration(now - (elapsedFrom || now))
          : formatDuration(totals[task.id] || 0);
        const subtitle = isForeign
          ? "Tracking on another device"
          : active
          ? "New Record"
          : "Total Spent";
        const proj = projectName(task.projectId);
        const borderColor = humanActive
          ? "var(--track-human)"
          : aiActive
          ? "var(--track-ai)"
          : "var(--border)";

        return (
          <div
            key={task.id}
            style={{ ...styles.row, borderColor }}
            onClick={() => navigate(`/taskDetail/${task.id}`)}
          >
            {/* Human play/stop (purple) on top, AI (blue) below — same stacked
                layout as the task card. */}
            <div style={styles.iconCol}>
              <button
                style={styles.iconButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (humanActive) requestStopTracking(task);
                  else setTaskTrackingState(task.id, task.projectId, true);
                }}
                aria-label={humanActive ? "Stop tracking" : "Resume tracking"}
              >
                {humanActive ? (
                  <StopIcon className="track-icon-human" style={styles.icon} />
                ) : (
                  <PlayIcon className="track-icon-human" style={styles.icon} />
                )}
              </button>
              {showAi && (
                <button
                  style={styles.iconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTaskAiTrackingState(task.id, task.projectId, !aiActive);
                  }}
                  aria-label={aiActive ? "Stop AI tracking" : "Resume AI tracking"}
                >
                  {aiActive ? (
                    <StopIcon className="track-icon-ai" style={styles.icon} />
                  ) : (
                    <PlayIcon className="track-icon-ai" style={styles.icon} />
                  )}
                </button>
              )}
            </div>

            <div style={styles.textCol}>
              <div style={styles.meta}>
                {proj && <span style={styles.project}>{proj} • </span>}
                <span style={isForeign ? styles.foreign : undefined}>
                  {subtitle}
                </span>
                <span style={styles.timeInline}> {timeLabel}</span>
              </div>
              <div style={styles.title}>
                {task.title || "Unnamed Task"}
                {actionLabels[task.id] ? ` · ${actionLabels[task.id]}` : ""}
              </div>
            </div>

            {/* Dismiss — only on stopped rows, like Android. */}
            {!active && (
              <button
                style={styles.closeButton}
                onClick={(e) => {
                  e.stopPropagation();
                  dismissTrackedTask(task.id);
                }}
                aria-label="Remove from tray"
                title="Remove"
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "16px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "7px 12px", // ~20% less vertical padding than before
    backgroundColor: "var(--card)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    cursor: "pointer",
  },
  iconCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px", // tighter spacing between the human/AI icons
    flexShrink: 0,
  },
  iconButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    flexShrink: 0,
  },
  icon: { width: "19px", height: "19px" }, // ~15% smaller
  textCol: { flex: 1, minWidth: 0 },
  meta: {
    fontSize: "11px",
    color: "var(--text-muted)",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  project: { color: "var(--text-muted)" },
  foreign: { color: "#ff9a9a" },
  timeInline: {
    color: "var(--primary)",
    fontWeight: "bold",
    fontVariantNumeric: "tabular-nums",
  },
  title: {
    color: "var(--text)",
    fontSize: "14px",
    fontWeight: "bold",
    lineHeight: 1.3,
    marginTop: "1px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  closeButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-faint)",
    fontSize: "15px",
    padding: "2px 4px",
    flexShrink: 0,
  },
};

export default TrackingTray;
